import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Gauge } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import { LOAD_SETS } from "../components/GoalLoad";

const SHADE = { gever: "bg-zinc-300", nemer: "bg-zinc-600", werkelijk: "bg-zinc-900" };
const nl = (n) => Number(n).toLocaleString("nl-NL");

function Bars({ row, max, testid }) {
  return (
    <div className="space-y-1 min-w-[180px]" data-testid={testid}>
      {LOAD_SETS.map((s) => (
        <div key={s.key} className="flex items-center gap-2 text-xs">
          <span className="w-16 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.short}</span>
          <div className="flex-1 h-2.5 bg-zinc-100">
            <div className={`h-full ${SHADE[s.key]} transition-[width] duration-500`} style={{ width: `${max ? (row[s.key].per_week / max) * 100 : 0}%` }} />
          </div>
          <span className="w-24 text-right tabular-nums"><span className="font-semibold">{nl(row[s.key].per_week)}</span> u/wk</span>
          <span className="w-24 text-right tabular-nums text-zinc-500 hidden sm:inline">{nl(row[s.key].totaal)} u tot.</span>
        </div>
      ))}
    </div>
  );
}

const maxOf = (rows) => Math.max(1, ...rows.flatMap((r) => LOAD_SETS.map((s) => r[s.key].per_week)));

export default function LoadOverviewPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orgId = params.get("org");
  const nodeId = params.get("node");
  const [orgs, setOrgs] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/organizations").then(({ data: d }) => setOrgs(d));
  }, []);

  useEffect(() => {
    if (nodeId) api.get(`/nodes/${nodeId}/load-overview`).then(({ data: d }) => setData(d), () => setData({ units: [], employees: [] }));
  }, [nodeId]);

  const uMax = data ? maxOf(data.units) : 1;
  const eMax = data ? maxOf(data.employees) : 1;

  return (
    <div className="min-h-screen bg-white" data-testid="load-overview-page">
      <Header orgs={orgs} currentOrgId={orgId} currentNodeId={nodeId} onSelectOrg={(id) => navigate(`/employees?org=${id}`)} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => navigate(`/employees?org=${orgId}&node=${nodeId}`)} className="font-mono text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="load-back">
          <ArrowLeft className="h-3 w-3" /> Terug naar medewerkers
        </button>
        <div className="border-b border-zinc-900 pb-6 mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Gauge className="h-3.5 w-3.5" /> {data?.node?.name || "…"}</div>
          <h1 className="font-grotesk text-4xl sm:text-5xl font-bold tracking-tight">Belasting</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">Opgeteld uit de subdoelen op dit niveau: uren per week × medewerkers, en het totaal over de weken. Per set: wat de opdrachtgever inschatte, wat de opdrachtnemer inschatte en hoe het werkelijk ging. Per medewerker is de belasting van een team- of afdelingsdoel gelijk verdeeld over de leden.</p>
          <div className="flex gap-4 mt-4 text-xs">
            {LOAD_SETS.map((s) => <span key={s.key} className="flex items-center gap-1.5"><span className={`h-2.5 w-4 ${SHADE[s.key]}`} /> {s.label}</span>)}
          </div>
        </div>
        {!data && <p className="font-mono text-sm text-zinc-500">Laden…</p>}
        {data && (
          <>
            <h2 className="font-grotesk text-xl font-bold mb-3">Per systeem en afdeling</h2>
            {data.units.length === 0 && <p className="text-sm text-zinc-400 mb-8">Geen Systeem I-, II- of IV-eenheden op dit niveau.</p>}
            <div className="divide-y divide-zinc-200 border-y border-zinc-900 mb-12" data-testid="load-units">
              {data.units.map((u) => (
                <div key={u.key} className="flex flex-col md:flex-row md:items-center gap-3 py-4" data-testid={`load-unit-${u.key}`}>
                  <div className="md:w-64 flex items-center gap-3">
                    <span className="h-8 w-10 bg-zinc-900 text-white font-grotesk font-bold text-sm flex items-center justify-center">{u.system}</span>
                    <div className="min-w-0">
                      <div className="font-grotesk font-semibold truncate">{u.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{u.members} mw · {u.goals} subdoelen</div>
                    </div>
                  </div>
                  <div className="flex-1"><Bars row={u} max={uMax} testid={`load-unit-bars-${u.key}`} /></div>
                </div>
              ))}
            </div>
            <h2 className="font-grotesk text-xl font-bold mb-3">Per medewerker</h2>
            {data.employees.length === 0 && <p className="text-sm text-zinc-400" data-testid="load-employees-empty">Nog geen belasting ingevuld bij subdoelen.</p>}
            <div className="divide-y divide-zinc-200 border-y border-zinc-900" data-testid="load-employees">
              {data.employees.map((e) => (
                <div key={e.id} className="flex flex-col md:flex-row md:items-center gap-3 py-3" data-testid={`load-employee-${e.id}`}>
                  <div className="md:w-64 min-w-0">
                    <div className="font-medium truncate">{e.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{e.units.join(" · ")}</div>
                  </div>
                  <div className="flex-1"><Bars row={e} max={eMax} testid={`load-employee-bars-${e.id}`} /></div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
